import { useBrokerContext } from '@/app/context';
import type { BrokerProductCategory } from '@/services/brokers/types';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { ValidationError } from '@/shared/exceptions/validation.error';
import {
  formatCurrencyFromString,
  validateCnpj,
  validateCpf,
  validateEndDate,
  normalizeEmail,
} from '@/shared/utils';
import { toaster } from '@/shared/ui/toaster.component';
import { isClonedQuoteMarkedAsDraft } from '@/shared/utils/clone-quote-status.util';
import { removeFormatRegisterNumber } from '@/shared/utils/remove-format-register-number';
import { useDebouncedValue } from '@/shared/utils/use-filters.hook';
import {
  Button,
  Field,
  RadioCard,
  Spinner,
  Switch,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { formOptions } from '@tanstack/react-form';
import { ArrowLeft, ArrowRight, FilePlus2, RefreshCcw } from 'lucide-react';
import { useEffect, useRef } from 'react';
import {
  AttachBusinessQuotePayload,
  CreateQuotePayload,
  Customer,
} from '../../../../services/quotes/types';
import { useQuoteParams } from '../../context/quote-params.context';
import { useQuoteSummary } from '../../context/quote-summary.context';
import { useCreateBusinessQuote } from '../../hooks/business/use-create-quote.hook';
import { useDiretrixDocumentData } from '../../hooks/common/use-diretrix-document-data.hook';
import { withForm } from '../../hooks/common/use-quote-form.hook';
import { useCreateQuote } from '../../hooks/homeowner/use-create-quote.hook';
import { usePersonData } from '../../hooks/homeowner/use-person-data.hook';
import { useUpdateBusinessQuote } from '../../hooks/business/use-update-quote.hook';
import { useUpdateQuote } from '../../hooks/homeowner/use-update-quote.hook';
import { useInsurerCompanies } from '../../hooks/common/use-insurer-companies.hook';
import { PropertyDeniedDialog } from './property-denied.dialog';

/** Normaliza PolicyId / ProposalNo se o JSON vier levemente diferente ou aninhado. */
function resolveIdsAfterCreate(data: { id?: string; quoteId?: string }): {
  policyId?: string;
  quoteId?: string;
} {
  const d = data as Record<string, unknown>;
  const nested = d.data as Record<string, unknown> | undefined;
  const policyId =
    (typeof d.id === 'string' && d.id.trim()) ||
    (typeof d.policyId === 'string' && d.policyId.trim()) ||
    (nested && typeof nested.id === 'string' && (nested.id as string).trim()) ||
    undefined;
  const quoteId =
    (typeof d.quoteId === 'string' && d.quoteId.trim()) ||
    (typeof d.quotationNo === 'string' && d.quotationNo.trim()) ||
    (typeof d.proposalNo === 'string' && d.proposalNo.trim()) ||
    (nested &&
      typeof nested.quoteId === 'string' &&
      (nested.quoteId as string).trim()) ||
    undefined;
  return { policyId, quoteId };
}

/**
 * Data mínima selecionável no calendário: ao editar cotação com início de vigência
 * no passado, `min` não pode ser só "hoje", senão o componente exibe/clampa para a data atual.
 */
function earliestIsoDate(a: string, b: string): string {
  const toTime = (iso: string) =>
    new Date(`${iso.split('T')[0]}T12:00:00`).getTime();
  const ta = toTime(a);
  const tb = toTime(b);
  if (Number.isNaN(ta) && Number.isNaN(tb)) return a;
  if (Number.isNaN(ta)) return b;
  if (Number.isNaN(tb)) return a;
  return ta <= tb ? a : b;
}

function latestIsoDate(a: string, b: string): string {
  const toTime = (iso: string) =>
    new Date(`${iso.split('T')[0]}T12:00:00`).getTime();
  const ta = toTime(a);
  const tb = toTime(b);
  if (Number.isNaN(ta) && Number.isNaN(tb)) return a;
  if (Number.isNaN(ta)) return b;
  if (Number.isNaN(tb)) return a;
  return ta >= tb ? a : b;
}

function DocumentWatcher({
  value,
  isLegalPerson,
  onResult,
}: {
  value: string;
  isLegalPerson: boolean;
  onResult: (data: {
    name?: string;
    socialName?: string;
    isLegalPerson: boolean;
    gender?: string;
    birthDate?: string;
  }) => void;
}) {
  const clean = removeFormatRegisterNumber(value);
  const isValidCpf =
    !isLegalPerson && clean.length === 11 && validateCpf(value);
  const isValidCnpj =
    isLegalPerson && clean.length === 14 && validateCnpj(value);
  const isValid = isValidCpf || isValidCnpj;
  const debouncedDoc = useDebouncedValue(isValid ? clean : '', 500);
  const prevDebouncedDoc = useRef(debouncedDoc);

  // CPF segue no I4Pro: é de lá que vêm sexo e data de nascimento.
  const { data: personData, isSuccess } = usePersonData(
    !isLegalPerson && debouncedDoc ? debouncedDoc : undefined
  );

  // CNPJ usa a mesma consulta do Capital Global — Diretrix
  // (`quote-engine-data`, via Compliance API) — e não o cadastro do I4Pro.
  const { data: diretrixData, isSuccess: isDiretrixSuccess } =
    useDiretrixDocumentData({
      documentNumber: value,
      useIndividualDocument: false,
      enabled: isLegalPerson,
    });

  useEffect(() => {
    const prev = prevDebouncedDoc.current;
    if (prev === debouncedDoc) return;
    prevDebouncedDoc.current = debouncedDoc;

    if (prev !== '') {
      onResult({
        name: undefined,
        socialName: undefined,
        isLegalPerson,
        gender: undefined,
        birthDate: undefined,
      });
    }
  }, [debouncedDoc]);

  useEffect(() => {
    if (isLegalPerson) return;
    if (!isSuccess || !debouncedDoc || !personData?.name) return;
    const gender =
      personData.gender === 1 ? 'M' : personData.gender === 2 ? 'F' : undefined;
    onResult({
      name: personData.name,
      socialName: personData.socialName || undefined,
      isLegalPerson,
      gender,
      birthDate: personData.birthDate || undefined,
    });
  }, [personData, isSuccess, debouncedDoc, isLegalPerson]);

  useEffect(() => {
    if (!isLegalPerson || !isDiretrixSuccess || !debouncedDoc) return;
    const legalName =
      diretrixData?.documentType === 'cnpj'
        ? diretrixData.legalName?.trim()
        : undefined;
    if (!legalName) return;

    onResult({ name: legalName, isLegalPerson });
  }, [diretrixData, isDiretrixSuccess, debouncedDoc, isLegalPerson]);

  return null;
}

const formOpts = formOptions({
  defaultValues: {
    isLegalPerson: false,
    name: '',
    hasSocialName: false,
    socialName: '',
    registerNumber: '',
    phone: '',
    email: '',
    congener: '',
    policyNumber: '',
    bonus: '-1',
    issuanceType: '1' as '1' | '3',
    startDate: '',
    endDate: '',
    billingCep: '',
    billingStreet: '',
    billingNumber: '',
    billingComplement: '',
    billingNeighborhood: '',
    billingCity: '',
    billingState: '',
    useBillingAsRiskAddress: false,
    renewalInsurer: '',
    renewalPolicyNumber: '',
    renewalBonus: '',
    claim: '',
    claimValue: '',
    // Capturado do I4Pro, não exibido na cotação — apenas na proposta
    gender: '',
    birthDate: '',
  },
});

export const StepInsuredData = withForm({
  ...formOpts,
  props: {
    onNext: (_policyId?: string, _quoteId?: string) => {},
    onBack: () => {},
    productId: '',
    productCode: '',
    category: 'HOME_INSURANCE' as BrokerProductCategory,
    stepCode: '',
    isViewMode: false,
    skipBackendSubmit: false,
  },
  render: ({
    form,
    onNext,
    onBack,
    productId,
    productCode,
    category,
    stepCode,
    isViewMode,
    skipBackendSubmit,
  }) => {
    const { selectedBroker } = useBrokerContext();
    const {
      id,
      productId: contextProductId,
      productCode: contextProductCode,
      category: contextCategory,
    } = useQuoteParams();

    const isBusinessInsurance =
      (contextCategory || category) === 'BUSINESS_INSURANCE';

    const {
      mutateAsync: createHomeownerAsync,
      isPending: isCreatingHomeowner,
    } = useCreateQuote();
    const {
      mutateAsync: createBusinessAsync,
      isPending: isCreatingBusiness,
      isError: isCreateBusinessError,
      error: createBusinessError,
    } = useCreateBusinessQuote();
    const isCreating = isCreatingHomeowner || isCreatingBusiness;

    const { mutateAsync: updateHomeownerAsync, isPending: isUpdatingHomeowner } =
      useUpdateQuote();
    const { mutateAsync: updateBusinessAsync, isPending: isUpdatingBusiness } =
      useUpdateBusinessQuote();
    const updateMutateAsync = isBusinessInsurance
      ? updateBusinessAsync
      : updateHomeownerAsync;
    const isUpdating = isUpdatingHomeowner || isUpdatingBusiness;
    const isPending = isCreating || isUpdating;
    const { updateProponent, updateInsuranceData, summary } = useQuoteSummary();
    const isClonedDraft = isClonedQuoteMarkedAsDraft(id);
    const { data: insurerCompanies } = useInsurerCompanies();

    useEffect(() => {
      // Empresarial nasce como PJ por padrão apenas na criação; na edição o
      // valor vem do prefill (documentType do cliente) e não pode ser
      // sobrescrito.
      if (!isBusinessInsurance || id) return;
      form.setFieldValue('isLegalPerson', true);
    }, [form, isBusinessInsurance, id]);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDate = today.toISOString().split('T')[0];

    const hasId = !!id;

    const handleDocumentResult = useRef(
      (data: {
        name?: string;
        socialName?: string;
        isLegalPerson: boolean;
        gender?: string;
        birthDate?: string;
      }) => {
        if (data.isLegalPerson) {
          // CNPJ: preenche "Razão Social" (campo name) com socialName retornado
          form.setFieldValue('name', data.socialName || data.name || '');
          form.setFieldValue('gender', '');
          form.setFieldValue('birthDate', '');
        } else {
          // CPF: preenche nome, nome social e captura sexo/nascimento (sem exibir)
          form.setFieldValue('name', data.name ?? '');
          if (data.socialName) {
            form.setFieldValue('hasSocialName', true);
            form.setFieldValue('socialName', data.socialName);
          } else {
            form.setFieldValue('hasSocialName', false);
            form.setFieldValue('socialName', '');
          }
          form.setFieldValue('gender', data.gender ?? '');
          form.setFieldValue('birthDate', data.birthDate ?? '');
        }
      }
    ).current;

    const persistProponentSummary = () => {
      const isLegalPerson = form.state.values.isLegalPerson;
      const hasSocialName = form.state.values.hasSocialName;
      const baseName = form.state.values.name || '';
      const socialName = form.state.values.socialName || '';
      const resolvedSocialName =
        !isLegalPerson && hasSocialName && socialName ? socialName : '';

      updateProponent({
        name: baseName || resolvedSocialName,
        document: form.state.values.registerNumber || '',
        personType: isLegalPerson ? 'PJ' : 'PF',
        socialName: resolvedSocialName,
      });
    };

    const persistInsuranceData = () => {
      const insuranceType: 'new' | 'renewal' =
        form.state.values.issuanceType === '3' ? 'renewal' : 'new';

      const base = {
        validityStart: form.state.values.startDate,
        validityEnd: form.state.values.endDate,
        expirationDate: form.state.values.endDate,
        insuranceType,
        congener: form.state.values.congener,
        policyNumber: form.state.values.policyNumber,
      };

      if (hasId) {
        // Edição: mantém data e usuário de inclusão originais (resumo já veio da API)
        updateInsuranceData({
          ...base,
          inclusionDate:
            summary.insurance.inclusionDate ?? new Date().toISOString(),
          inclusionUser: summary.insurance.inclusionUser ?? '',
        });
      } else {
        // Nova cotação: data atual; "Usuário de Inclusão" vem do insertByUserName na API (passo cálculo)
        updateInsuranceData({
          ...base,
          inclusionDate: new Date().toISOString(),
        });
      }
    };

    const handleSubmit = async () => {
      type FieldName = Parameters<typeof form.validateField>[0];

      const stepFields: FieldName[] = [
        'registerNumber',
        'name',
        'startDate',
        'endDate',
      ];

      if (form.state.values.issuanceType === '3') {
        // `policyNumber` é opcional — só a congênere bloqueia o avanço.
        stepFields.push('congener');
      }

      const validationResults = await Promise.all(
        stepFields.map(field => form.validateField(field, 'change'))
      );

      const hasErrors = validationResults.some(
        errors => Array.isArray(errors) && errors.length > 0
      );

      if (hasErrors) {
        return;
      }

      // Ao editar, o calendário aceita o início de vigência original mesmo no
      // passado (minForVigencia); aqui bloqueia o avanço nesse caso.
      const startDateOnly = (form.state.values.startDate || '').split('T')[0];
      if (!isViewMode && startDateOnly && startDateOnly < minDate) {
        toaster.create({
          type: 'error',
          title: 'Início de vigência inválido',
          description:
            'A data de início da vigência não pode estar no passado. Atualize a vigência para continuar.',
        });
        return;
      }

      if (skipBackendSubmit) {
        persistProponentSummary();
        persistInsuranceData();
        onNext?.();
        return;
      }
      const baseCustomer = {
        registerNumber: removeFormatRegisterNumber(
          form.state.values.registerNumber
        ),
        idType: form.state.values.isLegalPerson ? '2' : '1',
        name: form.state.values.name,
        socialName:
          !form.state.values.isLegalPerson && form.state.values.hasSocialName
            ? form.state.values.socialName
            : '',
      };

      const isLegalPerson = form.state.values.isLegalPerson;

      const customer: Customer = hasId
        ? {
            ...baseCustomer,
            documentNumber: removeFormatRegisterNumber(
              form.state.values.registerNumber
            ),
            documentType: isLegalPerson ? '2' : '1',
            cellphone: form.state.values.phone || '',
            phone: form.state.values.phone || '',
            email: normalizeEmail(form.state.values.email),
            isInsured: 'Y',
            isPolicyHolder: 'Y',
            isOrgParty: 'N',
            genderCode: isLegalPerson ? '' : form.state.values.gender || '',
            ...(isLegalPerson
              ? {}
              : { dateOfBirth: form.state.values.birthDate || '' }),
          }
        : ({
            ...baseCustomer,
            congener: form.state.values.congener || '',
            ...(isLegalPerson
              ? {}
              : {
                  gender: form.state.values.gender || '',
                  dateOfBirth: form.state.values.birthDate || '',
                }),
          } as Customer);

      const parsedClaimValue = parseInt(
        (form.state.values.claimValue || '').replace(/\D/g, ''),
        10
      );
      const sinisterValue = Number.isFinite(parsedClaimValue)
        ? parsedClaimValue / 100
        : 0;
      const sinisterPayload = {
        sinister: form.state.values.claim || undefined,
        sinisterValue: sinisterValue > 0 ? sinisterValue : undefined,
      };

      try {
        if (hasId) {
          const isRenewal = form.state.values.issuanceType === '3';
          const updatePayload: Partial<AttachBusinessQuotePayload> = {
            step: stepCode || 'CLIENT_INFO',
            effectiveDate: form.state.values.startDate,
            terminateDate: form.state.values.endDate,
            typeOfInsurance: form.state.values.issuanceType || '1',
            previousPolicyNumber: form.state.values.policyNumber || '',
            ...(isRenewal ? { bonus: form.state.values.bonus || '-1' } : {}),
            previousInsurer: form.state.values.congener || '',
            customer,
            ...(isBusinessInsurance && isRenewal ? sinisterPayload : {}),
          };

          const updateResult = await updateMutateAsync({
            id,
            payload: updatePayload,
          });

          if (updateResult.success) {
            persistProponentSummary();
            persistInsuranceData();
            onNext?.();
          }

          return;
        }

        const resolvedProductId = contextProductId || productId;
        const numericProductId = Number(resolvedProductId);

        if (!resolvedProductId || Number.isNaN(numericProductId)) {
          return;
        }
        const agents = selectedBroker
          ? [
              {
                name: selectedBroker.name,
                agentCode: selectedBroker.code || '',
                shareRate: 0,
                commission: 0,
                advisoryId: selectedBroker.advisoryId || '',
                registerNumber: selectedBroker.licenseNumber || '',
              },
            ]
          : undefined;

        const basePayload: CreateQuotePayload = {
          productId: numericProductId,
          productCode: contextProductCode || productCode || '',
          effectiveDate: form.state.values.startDate,
          terminateDate: form.state.values.endDate,
          quoteDate: form.state.values.startDate,
          orgCode: 'alba',
          productVersion: '1.0',
          ...(form.state.values.issuanceType === '3'
            ? { bonus: form.state.values.bonus || '-1' }
            : {}),
          previousInsurer: form.state.values.congener || '',
          previousPolicyNumber: form.state.values.policyNumber || '',
          customer,
          typeOfInsurance: form.state.values.issuanceType || '1',
          agents,
        };

        const createResult = isBusinessInsurance
          ? await createBusinessAsync({
              ...basePayload,
              ...sinisterPayload,
            })
          : await createHomeownerAsync(basePayload);

        if (createResult.success && createResult.data) {
          persistProponentSummary();
          persistInsuranceData();
          const { policyId, quoteId } = resolveIdsAfterCreate(
            createResult.data
          );
          if (policyId) {
            onNext?.(policyId, quoteId);
          }
        }
      } catch (error) {
        // ValidationError (recusa do empresarial) tem diálogo próprio. O resto
        // (ex.: 403 de corretora inativa) sumia sem aviso.
        if (error instanceof ValidationError) return;
        toaster.create({
          type: 'error',
          title: 'Não foi possível salvar a cotação',
          description:
            error instanceof Error && error.message.trim()
              ? error.message
              : 'Erro inesperado',
        });
      }
    };

    return (
      <div className='space-y-6'>
        <div className='flex flex-col gap-4'>
          <Text className='text-lg font-bold text-alba-black-500'>
            Dados da Cotação
          </Text>
          <div className='space-y-2'>
            <Field.Root>
              <Field.Label>Tipo de Seguro</Field.Label>
              <form.AppField
                name='issuanceType'
                children={field => (
                  <RadioCard.Root
                    value={field.state.value || '1'}
                    onValueChange={details => {
                      const value = details.value as '1' | '3';
                      field.handleChange(value);
                      // Seguro Novo não tem dados de renovação: reseta valor e
                      // meta (erros/touched) para não enviar valores antigos nem
                      // exibir erro de obrigatório ao voltar para renovação
                      if (value === '1') {
                        form.resetField('congener');
                        form.resetField('policyNumber');
                        form.resetField('bonus');
                        form.resetField('claim');
                        form.resetField('claimValue');
                      }
                    }}
                    disabled={isViewMode}
                  >
                    <div className='flex gap-2'>
                      <RadioCard.Item value='1' className=''>
                        <RadioCard.ItemControl className='flex-row items-center gap-2'>
                          <FilePlus2 className='w-6 h-6' strokeWidth={1.6} />
                          <RadioCard.ItemText>Seguro Novo</RadioCard.ItemText>
                        </RadioCard.ItemControl>
                        <RadioCard.ItemHiddenInput />
                      </RadioCard.Item>
                      <RadioCard.Item value='3' className=''>
                        <RadioCard.ItemControl className='flex-row items-center gap-2'>
                          <RefreshCcw className='w-6 h-6' strokeWidth={1.6} />
                          <RadioCard.ItemText>
                            Renovação Externa
                          </RadioCard.ItemText>
                        </RadioCard.ItemControl>
                        <RadioCard.ItemHiddenInput />
                      </RadioCard.Item>
                    </div>
                  </RadioCard.Root>
                )}
              />
            </Field.Root>
          </div>

          <form.Subscribe
            selector={state => state.values.startDate}
            children={startDateValue => {
              const inclusionDate =
                summary.insurance.inclusionDate?.split('T')[0] || '';
              const minFromInclusion = inclusionDate
                ? latestIsoDate(minDate, inclusionDate)
                : minDate;
              const minForVigencia =
                hasId && !isClonedDraft && startDateValue
                  ? earliestIsoDate(minDate, startDateValue)
                  : minFromInclusion;

              return (
                <div className='grid grid-cols-1 lg:grid-cols-2 gap-10'>
                  <form.AppField
                    name='startDate'
                    validators={{
                      onChange: ({ value }) => {
                        if (!value) return 'Início da vigência é obrigatório';
                        if (value < minForVigencia) {
                          return `Início da vigência deve ser a partir de ${minForVigencia.split('-').reverse().join('/')}`;
                        }
                        return undefined;
                      },
                    }}
                    children={field => {
                      return (
                        <field.CalendarField
                          label='Início da Vigência'
                          placeholder='Selecione a data'
                          required
                          minDate={minForVigencia}
                          disabled={isViewMode}
                        />
                      );
                    }}
                  />
                  <form.AppField
                    name='endDate'
                    validators={{
                      onBlur: validateEndDate(form.state.values.startDate),
                      onChange: validateEndDate(form.state.values.startDate),
                    }}
                    children={field => (
                      <field.CalendarField
                        label='Fim da Vigência'
                        placeholder='Selecione a data'
                        minDate={minForVigencia}
                        disabled
                      />
                    )}
                  />
                </div>
              );
            }}
          />

          <form.Subscribe
            selector={state => ({
              startDate: state.values.startDate,
              endDate: state.values.endDate,
            })}
            children={({ startDate, endDate }) => {
              const inclusionDate =
                summary.insurance.inclusionDate?.split('T')[0] || '';
              const minFromInclusion = inclusionDate
                ? latestIsoDate(minDate, inclusionDate)
                : minDate;
              const minStartDate =
                hasId && !isClonedDraft && startDate
                  ? earliestIsoDate(minDate, startDate)
                  : minFromInclusion;

              if (!startDate) {
                if (endDate) {
                  setTimeout(() => {
                    form.setFieldValue('endDate', '');
                  }, 0);
                }
                return null;
              }

              const start = new Date(startDate);

              if (Number.isNaN(start.getTime())) {
                return null;
              }

              if (startDate < minStartDate) {
                setTimeout(() => {
                  form.setFieldValue('startDate', minStartDate);
                }, 0);
                return null;
              }

              const end = new Date(start);
              end.setFullYear(end.getFullYear() + 1);

              const endDateFormatted = end.toISOString().split('T')[0];

              if (endDate !== endDateFormatted) {
                setTimeout(() => {
                  form.setFieldValue('endDate', endDateFormatted);
                }, 0);
              }

              return null;
            }}
          />

          <form.Subscribe
            selector={state => state.values.issuanceType}
            children={issuanceType =>
              issuanceType === '3' ? (
                <div className='space-y-4 mt-2'>
                  <div className='grid grid-cols-1 lg:grid-cols-2 gap-10'>
                    <form.AppField
                      name='congener'
                      validators={{
                        onChange: ({ value }) => {
                          if (!value || value.trim() === '') {
                            return 'Congênere é obrigatório';
                          }
                          return undefined;
                        },
                      }}
                      children={field => (
                        <field.SelectField
                          label='Congênere'
                          placeholder='Selecione a Congênere'
                          required
                          disabled={isViewMode || isPending}
                          options={
                            insurerCompanies?.map(c => ({
                              value: String(c.id),
                              label: c.name,
                            })) ?? []
                          }
                        />
                      )}
                    />

                    {/* Opcional: quando não informado, a emissão envia o
                        placeholder acordado com o I4Pro em `cdApoliceAnterior`. */}
                    <form.AppField
                      name='policyNumber'
                      children={field => (
                        <field.TextField
                          label='Número da Apólice'
                          placeholder='Digite o número da apólice'
                          maxLength={FIELD_MAX_LENGTHS.policyNumber}
                          disabled={isViewMode}
                        />
                      )}
                    />
                  </div>

                  <div className='grid grid-cols-1 lg:grid-cols-2 gap-10'>
                    <div className='grid grid-cols-1 space-y-4'>
                      <form.AppField
                        name='bonus'
                        defaultValue='-1'
                        children={field => (
                          <field.SelectField
                            label='Período de Experiência'
                            placeholder='Selecione o período'
                            disabled={isViewMode || isPending}
                            options={[
                              { value: '2', label: 'Um ano sem sinistro' },
                              { value: '3', label: 'Dois anos sem sinistro' },
                              { value: '4', label: 'Três anos sem sinistro' },
                              { value: '5', label: 'Quatro anos sem sinistro' },
                              { value: '6', label: 'Cinco anos sem sinistro' },
                              { value: '-1', label: 'Renovação com sinistro' },
                            ]}
                          />
                        )}
                      />

                      {isBusinessInsurance && (
                        <form.AppField
                          name='claimValue'
                          children={field => (
                            <Field.Root
                              invalid={!!field.state.meta.errors.length}
                              disabled={isViewMode || isPending}
                            >
                              <Field.Label>Valor do Sinistro</Field.Label>
                              <Field.Input
                                value={field.state.value || ''}
                                onChange={e =>
                                  field.handleChange(
                                    formatCurrencyFromString(e.target.value)
                                  )
                                }
                                onBlur={field.handleBlur}
                                placeholder='R$ 0,00'
                                disabled={isViewMode || isPending}
                              />
                              {field.state.meta.errors.length > 0 && (
                                <Field.ErrorText>
                                  {field.state.meta.errors[0]}
                                </Field.ErrorText>
                              )}
                            </Field.Root>
                          )}
                        />
                      )}
                    </div>

                    {isBusinessInsurance && (
                      <form.AppField
                        name='claim'
                        children={field => (
                          <field.TextareaField
                            label='Sinistro'
                            placeholder='Digite informações sobre o sinistro'
                            disabled={isViewMode || isPending}
                            rows={4}
                          />
                        )}
                      />
                    )}
                  </div>
                </div>
              ) : null
            }
          />
        </div>

        <div>
          <Text className='text-lg font-bold text-alba-black-500'>
            Dados do Proponente
          </Text>
        </div>

        <form.Subscribe
          selector={state => ({
            registerNumber: state.values.registerNumber,
            isLegalPerson: state.values.isLegalPerson,
          })}
          children={({ registerNumber, isLegalPerson }) => (
            <DocumentWatcher
              value={registerNumber}
              isLegalPerson={isLegalPerson}
              onResult={handleDocumentResult}
            />
          )}
        />

        <form.AppField
          name='isLegalPerson'
          children={field => (
            <div className='flex items-center gap-3'>
              <Switch.Root
                checked={field.state.value}
                onCheckedChange={details => {
                  field.handleChange(details.checked);
                  void form.validateField('registerNumber', 'change');
                  void form.validateField('name', 'change');
                }}
                disabled={isViewMode}
              >
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
                <Switch.Label>Pessoa Jurídica</Switch.Label>
                <Switch.HiddenInput />
              </Switch.Root>
            </div>
          )}
        />

        <div className='space-y-4'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
            <form.AppField
              name='registerNumber'
              validators={{
                onChange: ({ value, fieldApi }) => {
                  const isPJ = fieldApi.form.getFieldValue('isLegalPerson');
                  if (isPJ) {
                    if (!value) return 'CNPJ é obrigatório';
                    if (!validateCnpj(value)) return 'CNPJ inválido';
                    return undefined;
                  }
                  if (!value) return 'CPF é obrigatório';
                  if (!validateCpf(value)) return 'CPF inválido';
                  return undefined;
                },
              }}
              children={field => (
                <form.Subscribe
                  selector={state => state.values.isLegalPerson}
                  children={isPJ => (
                    <field.TextField
                      label={isPJ ? 'CNPJ' : 'CPF'}
                      placeholder={
                        isPJ ? 'XX.XXX.XXX/XXXX-XX' : 'XXX.XXX.XXX-XX'
                      }
                      required
                      maxLength={
                        isPJ ? FIELD_MAX_LENGTHS.cnpj : FIELD_MAX_LENGTHS.cpf
                      }
                      mask={isPJ ? 'cnpj' : 'cpf'}
                      disabled={isViewMode}
                    />
                  )}
                />
              )}
            />

            <form.AppField
              name='name'
              validators={{
                onChange: ({ value, fieldApi }) => {
                  const isPJ = fieldApi.form.getFieldValue('isLegalPerson');
                  if (isPJ) {
                    if (!value) return 'Razão Social é obrigatória';
                    return undefined;
                  }
                  if (!value) return 'Nome é obrigatório';
                  return undefined;
                },
              }}
              children={field => (
                <form.Subscribe
                  selector={state => state.values.isLegalPerson}
                  children={isPJ => (
                    <field.TextField
                      label={isPJ ? 'Razão Social' : 'Nome'}
                      placeholder='Digite o nome do proponente'
                      required
                      maxLength={
                        isPJ
                          ? FIELD_MAX_LENGTHS.companyName
                          : FIELD_MAX_LENGTHS.personName
                      }
                      disabled={isViewMode}
                    />
                  )}
                />
              )}
            />
          </div>

          <form.Subscribe
            selector={state => state.values.isLegalPerson}
            children={isPJ =>
              isPJ ? null : (
                <>
                  <form.AppField
                    name='hasSocialName'
                    children={field => (
                      <field.CheckboxField
                        label='Possui Nome Social?'
                        disabled={isViewMode}
                      />
                    )}
                  />

                  <form.Subscribe
                    selector={state => state.values.hasSocialName}
                    children={hasSocialName =>
                      hasSocialName ? (
                        <form.AppField
                          name='socialName'
                          children={field => (
                            <field.TextField
                              label='Nome Social'
                              placeholder='Digite o nome social do proponente'
                              maxLength={FIELD_MAX_LENGTHS.socialName}
                              disabled={isViewMode}
                            />
                          )}
                        />
                      ) : null
                    }
                  />
                </>
              )
            }
          />
        </div>

        <div className='flex flex-row items-center justify-between gap-3 self-stretch sm:gap-6 sm:px-6'>
          <Button
            variant='ghost'
            size='md'
            className='flex h-12 min-w-0 items-center justify-start gap-2 px-0 sm:h-auto'
            onClick={onBack}
          >
            <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
            Voltar
          </Button>
          <Button
            variant='primary'
            size='md'
            className='flex h-12 min-w-[132px] items-center justify-center gap-2 rounded-xl px-5 py-2 sm:h-auto sm:min-w-[148px] sm:px-6'
            onClick={handleSubmit}
            disabled={isPending}
          >
            {isPending ? (
              <>
                Processando...
                <Spinner size='sm' colorScheme='gray' />
              </>
            ) : (
              <>
                Próximo
                <ArrowRight className='w-6 h-6' strokeWidth={2} />
              </>
            )}
          </Button>
        </div>

        <PropertyDeniedDialog
          isOpen={
            isCreateBusinessError &&
            createBusinessError instanceof ValidationError &&
            !!createBusinessError.detail
          }
          detail={
            createBusinessError instanceof ValidationError
              ? createBusinessError.detail
              : undefined
          }
          errors={
            createBusinessError instanceof ValidationError
              ? createBusinessError.messages
              : []
          }
        />
      </div>
    );
  },
});
